import {
  SOCIAL_POST_CAROUSEL_MAX_SLIDES,
  SOCIAL_POST_CAROUSEL_MAX_TEMPLATE_OPTIONS,
  SOCIAL_POST_CAROUSEL_MIN_SLIDES,
  brandSocialAffinitiesSchema,
  collectLegacySocialPostCarouselTemplateOptions,
  collectSocialPostCarouselFormatCtaPatternIssues,
  socialPostCarouselFormatPatternRowV1Schema,
  socialPostCarouselFormatSnapshotV1Schema,
  socialPostCarouselSlideRoleSchema,
  type BrandSocialAffinities,
  type SocialPostCarouselFormatPatternRowV1,
  type SocialPostCarouselTemplateOptionV1,
} from "@runcheetah/shared";
import { z } from "zod";

export interface CarouselFormatOutputLike {
  id: string;
  key: string;
  name: string;
  active: boolean;
  minSlides: number;
  maxSlides: number;
  pattern: SocialPostCarouselFormatPatternRowV1[];
  templateOptions: SocialPostCarouselTemplateOptionV1[] | null;
  classifierGuidance: string;
  example: string;
  socialAffinities?: BrandSocialAffinities;
  bannedWords: string[];
  sortOrder: number;
}

export type CarouselFormatWriteInput = {
  key: string;
  name: string;
  active: boolean;
  minSlides: number;
  maxSlides: number;
  pattern: SocialPostCarouselFormatPatternRowV1[];
  templateOptions: SocialPostCarouselTemplateOptionV1[];
  classifierGuidance: string;
  example: string;
  socialAffinities: BrandSocialAffinities;
  bannedWords: string[];
  sortOrder: number;
};

const slugKeyPattern = /^[a-z0-9]+(?:_[a-z0-9]+)*$/;

export const parseCarouselFormatBannedWords = (value: string): string[] =>
  Array.from(
    new Set(
      value
        .split(/[,\n]/)
        .map((word) => word.trim().toLowerCase())
        .filter(Boolean),
    ),
  ).toSorted();

export const carouselFormatPatternRowDraftSchema = socialPostCarouselFormatPatternRowV1Schema
  .safeExtend({
    role: socialPostCarouselSlideRoleSchema,
    title: z
      .string()
      .trim()
      .min(1, { message: "Pattern row title is required." })
      .max(80, { message: "Pattern row title must be 80 characters or fewer." }),
    guidance: z
      .string()
      .trim()
      .min(1, { message: "Pattern row guidance is required." })
      .max(800, { message: "Pattern row guidance must be 800 characters or fewer." }),
    required: z.boolean(),
  })
  .strict();
export type CarouselFormatPatternRowDraft = z.infer<typeof carouselFormatPatternRowDraftSchema>;

const carouselFormatDraftBaseSchema = z.object({
  id: z.string().min(1).nullable(),
  key: z
    .string()
    .trim()
    .min(1, { message: "Key is required." })
    .max(80, { message: "Key must be 80 characters or fewer." })
    .regex(slugKeyPattern, {
      message: "Key must use lowercase words separated by underscores.",
    }),
  name: z
    .string()
    .trim()
    .min(1, { message: "Name is required." })
    .max(100, { message: "Name must be 100 characters or fewer." }),
  active: z.boolean(),
  minSlides: z
    .number()
    .int()
    .min(SOCIAL_POST_CAROUSEL_MIN_SLIDES, {
      message: `Minimum slides must be at least ${SOCIAL_POST_CAROUSEL_MIN_SLIDES}.`,
    })
    .max(SOCIAL_POST_CAROUSEL_MAX_SLIDES, {
      message: `Minimum slides must be at most ${SOCIAL_POST_CAROUSEL_MAX_SLIDES}.`,
    }),
  maxSlides: z
    .number()
    .int()
    .min(SOCIAL_POST_CAROUSEL_MIN_SLIDES, {
      message: `Maximum slides must be at least ${SOCIAL_POST_CAROUSEL_MIN_SLIDES}.`,
    })
    .max(SOCIAL_POST_CAROUSEL_MAX_SLIDES, {
      message: `Maximum slides must be at most ${SOCIAL_POST_CAROUSEL_MAX_SLIDES}.`,
    }),
  pattern: z
    .array(carouselFormatPatternRowDraftSchema)
    .min(SOCIAL_POST_CAROUSEL_MIN_SLIDES, {
      message: `Pattern must include at least ${SOCIAL_POST_CAROUSEL_MIN_SLIDES} rows.`,
    })
    .max(SOCIAL_POST_CAROUSEL_MAX_SLIDES, {
      message: `Pattern can include at most ${SOCIAL_POST_CAROUSEL_MAX_SLIDES} rows.`,
    }),
  templateOptions: z
    .array(
      z.object({
        templateId: z.string().uuid(),
        templateName: z.string().trim().min(1).max(120),
      }),
    )
    .max(SOCIAL_POST_CAROUSEL_MAX_TEMPLATE_OPTIONS),
  classifierGuidance: z
    .string()
    .trim()
    .min(1, { message: "Classifier guidance is required." })
    .max(1200, { message: "Classifier guidance must be 1200 characters or fewer." }),
  example: z
    .string()
    .trim()
    .min(1, { message: "Example is required." })
    .max(2000, { message: "Example must be 2000 characters or fewer." }),
  socialAffinities: brandSocialAffinitiesSchema,
  bannedWordsText: z.string().superRefine((value, ctx) => {
    for (const word of parseCarouselFormatBannedWords(value)) {
      if (word.length > 80) {
        ctx.addIssue({
          code: "custom",
          message: "Banned words must be 80 characters or fewer.",
        });
        return;
      }
    }
  }),
  sortOrder: z
    .number()
    .int({ message: "Sort order must be a whole number." })
    .min(0, { message: "Sort order cannot be negative." })
    .max(10_000, { message: "Sort order must be 10000 or lower." }),
});

export type CarouselFormatDraft = z.infer<typeof carouselFormatDraftBaseSchema>;

const carouselFormatSlideRangeSchema = carouselFormatDraftBaseSchema.pick({
  minSlides: true,
  maxSlides: true,
});

const carouselFormatPatternRangeSchema = carouselFormatDraftBaseSchema.pick({
  minSlides: true,
  maxSlides: true,
  pattern: true,
});

export const buildCarouselFormatWriteInput = (
  draft: CarouselFormatDraft,
): CarouselFormatWriteInput => ({
  key: draft.key.trim(),
  name: draft.name.trim(),
  active: draft.active,
  minSlides: draft.minSlides,
  maxSlides: draft.maxSlides,
  pattern: draft.pattern.map((row) => ({
    role: row.role,
    title: row.title.trim(),
    guidance: row.guidance.trim(),
    required: row.required,
  })),
  templateOptions: draft.templateOptions,
  classifierGuidance: draft.classifierGuidance.trim(),
  example: draft.example.trim(),
  socialAffinities: brandSocialAffinitiesSchema.parse(draft.socialAffinities),
  bannedWords: parseCarouselFormatBannedWords(draft.bannedWordsText),
  sortOrder: draft.sortOrder,
});

const carouselFormatDraftLocalSchema = carouselFormatDraftBaseSchema
  .refine((draft) => draft.minSlides <= draft.maxSlides, {
    path: ["minSlides"],
    message: "Minimum slides cannot exceed maximum slides.",
    when: ({ value }) => carouselFormatSlideRangeSchema.safeParse(value).success,
  })
  .refine(
    (draft) => draft.pattern.length >= draft.minSlides && draft.pattern.length <= draft.maxSlides,
    {
      path: ["pattern"],
      message: "Pattern row count must fit within the selected slide range.",
      when: ({ value }) => carouselFormatPatternRangeSchema.safeParse(value).success,
    },
  );

export const carouselFormatDraftSchema = carouselFormatDraftLocalSchema.superRefine(
  (draft, ctx) => {
    if (!carouselFormatDraftLocalSchema.safeParse(draft).success) {
      return;
    }

    for (const issue of collectSocialPostCarouselFormatCtaPatternIssues(draft.pattern)) {
      ctx.addIssue({ code: "custom", path: issue.path, message: issue.message });
    }

    const snapshotResult = socialPostCarouselFormatSnapshotV1Schema.safeParse({
      type: "social_post.carousel_format_snapshot",
      version: 1,
      ...buildCarouselFormatWriteInput(draft),
    });
    if (!snapshotResult.success) {
      for (const issue of snapshotResult.error.issues) {
        ctx.addIssue({
          code: "custom",
          path: issue.path,
          message: issue.message,
        });
      }
    }
  },
);

export const carouselFormatWriteInputSchema = carouselFormatDraftSchema.transform(
  buildCarouselFormatWriteInput,
);

const DEFAULT_PATTERN: CarouselFormatPatternRowDraft[] = [
  {
    role: "hook",
    title: "Hook",
    guidance: "Open with the audience problem or payoff.",
    required: true,
    templateOptions: [],
  },
  {
    role: "cta",
    title: "Close",
    guidance: "End with one specific next step.",
    required: true,
    templateOptions: [],
  },
];

export const createEmptyCarouselFormatDraft = (sortOrder = 0): CarouselFormatDraft => ({
  id: null,
  key: "",
  name: "",
  active: true,
  minSlides: SOCIAL_POST_CAROUSEL_MIN_SLIDES,
  maxSlides: 5,
  pattern: DEFAULT_PATTERN.map((row) => ({ ...row })),
  templateOptions: [],
  classifierGuidance: "",
  example: "",
  socialAffinities: { styles: [], mechanics: [] },
  bannedWordsText: "",
  sortOrder,
});

export const formatCarouselFormatBannedWords = (words: string[]): string => words.join("\n");

export const createCarouselFormatDraftFromOutput = (
  format: CarouselFormatOutputLike,
): CarouselFormatDraft => ({
  id: format.id,
  key: format.key,
  name: format.name,
  active: format.active,
  minSlides: format.minSlides,
  maxSlides: format.maxSlides,
  pattern: format.pattern.map((row) => ({
    role: row.role,
    title: row.title,
    guidance: row.guidance,
    required: row.required,
    templateOptions: [],
  })),
  templateOptions:
    format.templateOptions ?? collectLegacySocialPostCarouselTemplateOptions(format.pattern),
  classifierGuidance: format.classifierGuidance,
  example: format.example,
  socialAffinities: brandSocialAffinitiesSchema.parse(format.socialAffinities ?? {}),
  bannedWordsText: formatCarouselFormatBannedWords(format.bannedWords),
  sortOrder: format.sortOrder,
});

export const createCarouselPatternRowDraft = (): CarouselFormatPatternRowDraft => ({
  role: "context",
  title: "",
  guidance: "",
  required: true,
  templateOptions: [],
});

export const moveArrayItem = <T>(items: T[], index: number, direction: -1 | 1): T[] => {
  const nextIndex = index + direction;
  if (index < 0 || nextIndex < 0 || index >= items.length || nextIndex >= items.length) {
    return items;
  }

  const next = [...items];
  const item = next[index]!;
  next[index] = next[nextIndex]!;
  next[nextIndex] = item;
  return next;
};

export const moveCarouselFormatId = (ids: string[], id: string, direction: -1 | 1): string[] =>
  moveArrayItem(ids, ids.indexOf(id), direction);

export const validateCarouselFormatDraft = (draft: CarouselFormatDraft): string[] => {
  const result = carouselFormatDraftSchema.safeParse(draft);
  return result.success
    ? []
    : Array.from(new Set(result.error.issues.map((issue) => issue.message)));
};
